import { useCallback } from "react";
import { API_BASE_URL } from "@/constants/app.constant";
import { useFetch } from "@/hooks/useFetch";
import useAuthService from "@/services/auth.service";
import { APIResponse } from "@/interfaces/common.interface";

export interface SystemPromptResponse extends APIResponse<string> {}

export interface AnalyzeFeedbackRequest {
  message_id: number;
  conversation_type: string;
}

export interface AnalyzeAllFeedbacksRequest {
  conversation_type: string;
}

export interface AnalyzeFeedbackResponse extends APIResponse<{
  rewrite_guidance: string;
}> {}

export interface RefinePromptRequest {
  prompt_type: string;
  ai_guidance: string;
  user_guidance?: string;
}

export interface RefinePromptResponse extends APIResponse<{
  refined_prompt: string;
}> {}

export interface SavePromptRequest {
  prompt_type: string;
  new_prompt: string;
  change_summary?: string;
}

export interface SavePromptResponse extends APIResponse<{
  version: number;
  prompt_type: string;
  message: string;
}> {}

/**
 * useFeedForwardAIService hook provides feedforward AI service API functions.
 * Handles system prompt management, feedback analysis, prompt refinement, and saving.
 * 
 * @hook
 * @returns {Object} Feedforward AI service methods
 * @returns {Function} returns.getSystemPrompt - Get current system prompt
 * @returns {Function} returns.analyzeAllFeedbacks - Analyze all pending feedbacks
 * @returns {Function} returns.analyzeFeedback - Analyze single feedback
 * @returns {Function} returns.refinePrompt - Refine prompt with AI and user guidance
 * @returns {Function} returns.savePrompt - Save refined prompt (creates backup)
 * 
 * @example
 * ```tsx
 * const { getSystemPrompt, analyzeFeedback, refinePrompt } = useFeedForwardAIService();
 * 
 * const prompt = await getSystemPrompt('chop_gpt');
 * const analysis = await analyzeFeedback({ message_id: 123, conversation_type: 'chop_gpt' });
 * ```
 */
const useFeedForwardAIService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();
  const FEEDFORWARD_API_BASE_URL = `${API_BASE_URL}/admin/feedforward`;

  /**
   * Gets the current system prompt for a prompt type.
   * 
   * @param {string} promptType - Type of prompt to retrieve
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<SystemPromptResponse>} System prompt response
   * @throws {Error} If token is invalid
   */
  const getSystemPrompt = useCallback(
    async (promptType: string, signal?: AbortSignal): Promise<SystemPromptResponse> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      const response = await fetchService.get<SystemPromptResponse>(
        `${FEEDFORWARD_API_BASE_URL}/system-prompt?prompt_type=${encodeURIComponent(promptType)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      return response;
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Analyzes all pending feedbacks for a conversation type and generates comprehensive rewrite guidance.
   * 
   * @param {AnalyzeAllFeedbacksRequest} request - Request with conversation_type
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<AnalyzeFeedbackResponse>} Analysis response with rewrite guidance
   * @throws {Error} If token is invalid or authentication fails
   */
  const analyzeAllFeedbacks = useCallback(
    async (
      request: AnalyzeAllFeedbacksRequest,
      signal?: AbortSignal
    ): Promise<AnalyzeFeedbackResponse> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token - please log in again");
      }

      try {
        const response = await fetchService.post<AnalyzeFeedbackResponse>(
          `${FEEDFORWARD_API_BASE_URL}/analyze-all`,
          {
            body: request,
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        return response;
      } catch (error: any) {
        console.error("analyzeAllFeedbacks error:", error);
        // Re-throw with more context
        if (error?.status === 401 || error?.message?.includes("Authorization")) {
          throw new Error("Authentication failed. Please refresh the page.");
        }
        throw error;
      }
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Analyzes a single feedback message and generates rewrite guidance.
   * 
   * @param {AnalyzeFeedbackRequest} request - Request with message_id and conversation_type
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<AnalyzeFeedbackResponse>} Analysis response with rewrite guidance
   * @throws {Error} If token is invalid
   */
  const analyzeFeedback = useCallback(
    async (
      request: AnalyzeFeedbackRequest,
      signal?: AbortSignal
    ): Promise<AnalyzeFeedbackResponse> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      const response = await fetchService.post<AnalyzeFeedbackResponse>(
        `${FEEDFORWARD_API_BASE_URL}/analyze`,
        {
          body: request,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      return response;
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Refines the system prompt based on AI guidance and optional user guidance.
   * 
   * @param {RefinePromptRequest} request - Request with prompt_type, ai_guidance, and optional user_guidance
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<RefinePromptResponse>} Response with refined prompt
   * @throws {Error} If token is invalid
   */
  const refinePrompt = useCallback(
    async (
      request: RefinePromptRequest,
      signal?: AbortSignal
    ): Promise<RefinePromptResponse> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      const response = await fetchService.post<RefinePromptResponse>(
        `${FEEDFORWARD_API_BASE_URL}/refine`,
        {
          body: request,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      return response;
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Saves the new system prompt (creates backup automatically).
   * 
   * @param {SavePromptRequest} request - Request with prompt_type, new_prompt, and optional change_summary
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<SavePromptResponse>} Response with version, prompt_type, and message
   * @throws {Error} If token is invalid
   */
  const savePrompt = useCallback(
    async (
      request: SavePromptRequest,
      signal?: AbortSignal
    ): Promise<SavePromptResponse> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      const response = await fetchService.post<SavePromptResponse>(
        `${FEEDFORWARD_API_BASE_URL}/save`,
        {
          body: request,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      return response;
    },
    [fetchService, getUserAccessToken]
  );

  return {
    getSystemPrompt,
    analyzeAllFeedbacks,
    analyzeFeedback,
    refinePrompt,
    savePrompt,
  };
};

export default useFeedForwardAIService;
